import { createFileRoute } from '@tanstack/react-router';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { z } from 'zod';

import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/dialog';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { subscriptionStore } from '@/features/subscription/subscription.store';
import { teamApis } from '@/features/team/team.query';
import { teamStore } from '@/features/team/team.store';

const JoinSearchSchema = z.object({
  token: z.string().optional(),
});

export const Route = createFileRoute('/team/join')({
  component: JoinTeamPage,
  validateSearch: JoinSearchSchema.parse,
  staticData: { requiresAuth: true },

  async beforeLoad({ context, search }) {
    await context.auth.requireAuth({
      pathname: '/team/join',
      search,
    });
  },
});

function JoinTeamPage() {
  return <JoinTeamPageContent />;
}

const JoinTeamPageContent = observer(function JoinTeamPageContent() {
  const { token } = Route.useSearch();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const doJoin = async () => {
    if (!token) {
      setError('Invalid invite link: missing token');
      return;
    }
    setError(null);
    try {
      await teamApis.joinTeam(token);
      setSuccess(true);
      setConfirmOpen(false);
      if (teamStore.dashboard) {
        window.location.href = '/';
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to join team');
    }
  };

  const handleJoin = () => {
    if (!token) {
      setError('Invalid invite link: missing token');
      return;
    }
    if (subscriptionStore.isPlus) {
      setConfirmOpen(true);
    } else {
      void doJoin();
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <YStack className="w-full max-w-sm gap-6 text-center">
        {success ? (
          <YStack className="gap-3 items-center">
            <div className="rounded-full bg-success-3 p-3">
              <Icons.check className="w-6 h-6 text-success-11" />
            </div>
            <h1 className="text-xl font-semibold text-foreground">
              You're in!
            </h1>
            <p className="text-sm text-foreground-muted">Redirecting...</p>
          </YStack>
        ) : (
          <YStack className="gap-6">
            <YStack className="gap-2">
              <h1 className="text-2xl font-semibold text-foreground tracking-tight">
                Join Team
              </h1>
              <p className="text-sm text-foreground-muted">
                Accept your invitation to join a Littlebird team.
              </p>
            </YStack>

            {error && (
              <XStack className="items-center gap-2 rounded-lg bg-danger-3 px-3 py-2 text-left">
                <Icons.warnTriangle className="w-3.5 h-3.5 text-danger-11 shrink-0" />
                <span className="text-sm text-danger-11">{error}</span>
              </XStack>
            )}

            <Button
              variant="default"
              size="lg"
              onClick={handleJoin}
              isLoading={teamStore.joinLoading}
              loadingText="Joining..."
              className="w-full"
            >
              Accept Invite
            </Button>
          </YStack>
        )}
      </YStack>

      <Dialog.Root open={confirmOpen} onOpenChange={setConfirmOpen}>
        <Dialog.Content className="max-w-sm">
          <Dialog.Header>
            <Dialog.Title>Join team?</Dialog.Title>
            <Dialog.Description>
              Your current paid subscription will be canceled and your access
              will be billed through the team going forward.
            </Dialog.Description>
          </Dialog.Header>
          <Dialog.Footer>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setConfirmOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              size="sm"
              isLoading={teamStore.joinLoading}
              onClick={doJoin}
            >
              Join team
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Root>
    </div>
  );
});
